import React, { useMemo } from "react";
import mainLogo from "../../../Image/Main_logo.png";
import {
  FaCalendarAlt,
  FaFileAlt,
  FaInfoCircle,
  FaArrowRight,
} from "react-icons/fa";
import "./PartyCallPrintReport.css";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

const PartyCallYearlyIndexSheet = ({
  party,
  year,
  selectedProjectName = null,
  activeMonths = [],
  pageNumber = 1,
  totalPages = 1,
  isLastPage = false,
  isScreenPreview = false,
  nowFormatted = null,
  onNavigateToPage = null,
}) => {
  const partyName = party?.CompanyName || "All Parties";
  const partyType = party?.Type || "CLIENT";

  const timeStamp = useMemo(() => {
    if (nowFormatted) return nowFormatted;
    const d = new Date();
    return `${formatDate(d)} at ${formatTime(d)}`;
  }, [nowFormatted]);

  // Calculate annual summary across active months
  const summary = useMemo(() => {
    let totalCalls = 0;
    let completed = 0;
    let pending = 0;
    let other = 0;

    (activeMonths || []).forEach((m) => {
      const stats = m.monthStats || {};
      totalCalls += stats.total || 0;
      completed += stats.completed || 0;
      pending += stats.pending || 0;
      other += stats.other || stats.inProgress || 0;
    });

    const resolutionRate =
      totalCalls > 0
        ? `${Math.round((completed / totalCalls) * 100)}%`
        : "100%";

    return {
      activeMonthsCount: (activeMonths || []).length,
      totalCalls,
      completed,
      pending,
      other,
      resolutionRate,
    };
  }, [activeMonths]);

  return (
    <div
      id="calendar-sheet-page-1"
      className={`print-page-1 print-index-sheet ${isLastPage ? "is-last-page" : ""
        } ${isScreenPreview ? "screen-preview-sheet" : ""}`}
    >
      {/* Screen Preview Page Tag */}
      {isScreenPreview && (
        <div className="screen-page-tag">
          Page 1 of {totalPages} • Annual Master Index &amp; Page Directory ({year})
        </div>
      )}

      {/* 1. Standardized Corporate Header */}
      <div className="print-header">
        <div className="print-header-brand">
          <img
            src={mainLogo}
            alt="Dynamic Softlink"
            className="print-header-logo"
          />
          <div className="print-header-titles">
            <h1>DYNAMIC SOFTLINK &ndash; ANNUAL CALENDAR &amp; ACTIVITY INDEX</h1>
            <p>Master Directory &bull; Page-Wise Month Navigation &amp; Support Audit</p>
          </div>
        </div>

        <div className="print-header-meta">
          <div className="party-name">
            {partyName}
            <span className="meta-pill">{partyType}</span>
          </div>
          <div>
            <strong>Period:</strong> Pure Year {year}
            {selectedProjectName && (
              <>
                {" "}
                &bull; <strong>Project:</strong> {selectedProjectName}
              </>
            )}
          </div>
          <div style={{ fontSize: "8px", color: "#64748b", marginTop: "1px" }}>
            Report Generated: {timeStamp}
          </div>
        </div>
      </div>

      {/* 2. Annual Overview KPI Banner */}
      <div className="print-index-kpi-banner">
        <div className="print-index-kpi-header">
          <div className="flex items-center gap-2">
            <span className="print-summary-title">
              Annual Support Overview &ndash; Year {year}
            </span>
            <span
              className="text-[9px] text-slate-500 font-medium"
              style={{ marginLeft: "4px" }}
            >
              &bull; Active Months: <strong>{summary.activeMonthsCount}</strong> of 12
            </span>
          </div>
          <div className="print-resolution-badge">
            Annual Resolution Rate: {summary.resolutionRate}
          </div>
        </div>

        <div className="print-summary-grid">
          <div className="print-summary-card card-total">
            <span className="label">Active Months</span>
            <span className="val">{summary.activeMonthsCount}</span>
          </div>
          <div className="print-summary-card card-total" style={{ borderLeftColor: "#0284c7" }}>
            <span className="label">Total Calls</span>
            <span className="val">{summary.totalCalls}</span>
          </div>
          <div className="print-summary-card card-comp">
            <span className="label">Completed</span>
            <span className="val">{summary.completed}</span>
          </div>
          <div className="print-summary-card card-prog">
            <span className="label">In Progress</span>
            <span className="val">{summary.other}</span>
          </div>
          <div className="print-summary-card card-pend">
            <span className="label">Pending</span>
            <span className="val">{summary.pending}</span>
          </div>
        </div>
      </div>

      {/* 3. Master Index & Page Navigation Table */}
      <div className="print-index-table-wrapper">
        <div className="print-section-sub-title">
          <span>Active Months Directory &amp; Calendar Page Index</span>
          <span
            style={{
              fontSize: "8px",
              fontWeight: 500,
              textTransform: "none",
              color: "#64748b",
              marginLeft: "6px",
            }}
          >
            (Jis month me call data hai sirf wahi niche darj hain &bull; Agle pages par inka detailed calendar dekhein)
          </span>
        </div>

        <table className="print-index-table">
          <thead>
            <tr>
              <th style={{ width: "30px", textAlign: "center" }}>#</th>
              <th>Month &amp; Year</th>
              <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Total Calls</th>
              <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Completed</th>
              <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>In Progress</th>
              <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Pending</th>
              <th style={{ textAlign: "center", whiteSpace: "nowrap" }}>Resolution Rate</th>
              <th style={{ textAlign: "center", whiteSpace: "nowrap" }}>Status</th>
              <th style={{ width: "125px", textAlign: "center", whiteSpace: "nowrap" }}>
                Calendar Page
              </th>
            </tr>
          </thead>
          <tbody>
            {(!activeMonths || activeMonths.length === 0) ? (
              <tr>
                <td
                  colSpan={9}
                  style={{
                    textAlign: "center",
                    padding: "16px 8px",
                    color: "#64748b",
                    fontSize: "9px",
                    fontStyle: "italic",
                  }}
                >
                  Saal {year} me is party ke liye koi call data darj nahi hai (0 Calls Logged).
                </td>
              </tr>
            ) : (
              activeMonths.map((m, idx) => {
                const targetPageNum = idx + 2;
                const stats = m.monthStats || {
                  total: 0,
                  completed: 0,
                  pending: 0,
                  other: 0,
                  resolutionRate: "100%",
                };
                const hasPending = stats.pending > 0;
                const isAllDone = stats.total > 0 && stats.completed === stats.total;

                return (
                  <tr key={m.month}>
                    <td
                      style={{
                        textAlign: "center",
                        fontWeight: 800,
                        color: "#64748b",
                      }}
                    >
                      {idx + 1}
                    </td>
                    <td style={{ fontWeight: 800, color: "#0f172a" }}>
                      <span className="inline-flex items-center gap-1.5">
                        <FaCalendarAlt style={{ color: "#154D75", fontSize: "8.5px" }} />
                        <span>
                          {m.monthName} {year}
                        </span>
                      </span>
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: 800,
                        color: "#0284c7",
                      }}
                    >
                      {stats.total}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: 700,
                        color: stats.completed > 0 ? "#059669" : "#94a3b8",
                      }}
                    >
                      {stats.completed}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: 700,
                        color: stats.other > 0 ? "#d97706" : "#94a3b8",
                      }}
                    >
                      {stats.other}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: 700,
                        color: stats.pending > 0 ? "#dc2626" : "#94a3b8",
                      }}
                    >
                      {stats.pending}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span
                        className={`print-table-rate-pill ${isAllDone
                          ? "rate-100"
                          : hasPending
                            ? "rate-pending"
                            : "rate-normal"
                          }`}
                      >
                        {stats.resolutionRate}
                      </span>
                    </td>
                    <td style={{ textAlign: "center", fontSize: "8px" }}>
                      {isAllDone ? (
                        <span style={{ color: "#059669", fontWeight: 800 }}>
                          ✓ All Done
                        </span>
                      ) : hasPending ? (
                        <span style={{ color: "#dc2626", fontWeight: 800 }}>
                          ● Has Pending
                        </span>
                      ) : (
                        <span style={{ color: "#d97706", fontWeight: 800 }}>
                          ◐ In Progress
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (isScreenPreview && onNavigateToPage) {
                            onNavigateToPage(targetPageNum, m.month);
                          }
                        }}
                        className={`index-page-badge ${isScreenPreview ? "index-page-btn" : ""
                          }`}
                        title={
                          isScreenPreview
                            ? `Click to scroll to Page ${targetPageNum} (${m.monthName} ${year} Calendar)`
                            : `Calendar sheet located on Page ${targetPageNum}`
                        }
                      >
                        <FaFileAlt style={{ fontSize: "7.5px" }} />
                        <span>Page {targetPageNum}</span>
                        {isScreenPreview && (
                          <FaArrowRight
                            style={{
                              fontSize: "6.5px",
                              marginLeft: "2px",
                              opacity: 0.7,
                            }}
                          />
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {activeMonths && activeMonths.length > 0 && (
            <tfoot>
              <tr className="print-index-total-row">
                <td
                  colSpan={2}
                  style={{
                    textAlign: "left",
                    fontWeight: 900,
                    textTransform: "uppercase",
                    color: "#0f172a",
                  }}
                >
                  Totals Across Active Months
                </td>
                <td
                  style={{
                    textAlign: "right",
                    fontWeight: 900,
                    color: "#0284c7",
                  }}
                >
                  {summary.totalCalls}
                </td>
                <td
                  style={{
                    textAlign: "right",
                    fontWeight: 900,
                    color: "#059669",
                  }}
                >
                  {summary.completed}
                </td>
                <td
                  style={{
                    textAlign: "right",
                    fontWeight: 900,
                    color: "#d97706",
                  }}
                >
                  {summary.other}
                </td>
                <td
                  style={{
                    textAlign: "right",
                    fontWeight: 900,
                    color: "#dc2626",
                  }}
                >
                  {summary.pending}
                </td>
                <td
                  style={{
                    textAlign: "center",
                    fontWeight: 900,
                    color: "#065f46",
                  }}
                >
                  {summary.resolutionRate}
                </td>
                <td
                  style={{
                    textAlign: "center",
                    fontWeight: 800,
                    color: "#154D75",
                  }}
                >
                  {summary.activeMonthsCount} Active {summary.activeMonthsCount === 1 ? "Month" : "Months"}
                </td>
                <td
                  style={{
                    textAlign: "center",
                    fontWeight: 900,
                    color: "#154D75",
                  }}
                >
                  Pages 2 to {totalPages}
                </td>
              </tr>
            </tfoot>
          )}
        </table>

        {/* Informational Filter Notice */}
        <div className="print-index-info-note">
          <FaInfoCircle style={{ fontSize: "9px", shrink: 0, color: "#0284c7" }} />
          <span>
            <strong>Report Filter Applied:</strong> Jin mahino me koi task ya call data darj nahi tha, unhe print/export report se hata diya gaya hai. Sirf upar darj active mahino ke full-page calendars aage diye gaye Page Numbers par shamil hain.
          </span>
        </div>
      </div>

      {/* 4. Page Footer */}
      <div className="print-page-footer">
        <span>Dynamic Softlink ERP Customer Support System &bull; Annual Calendar Master Index</span>
        <span>
          Page {pageNumber} of {totalPages} &bull; Directory &amp; Navigation Table
        </span>
      </div>
    </div>
  );
};

export default PartyCallYearlyIndexSheet;
